import { api } from "@/lib/api";
import { isQZAvailable, getBranchPrinter, printLinhas } from "@/lib/qzTray";

/**
 * Conferência de consumo (pré-conta): o papel que vai à mesa antes do
 * pagamento. Não é documento fiscal e não mexe na comanda.
 *
 * O backend monta o papel (conferencia_lib.py) — gorjeta, agrupamento e total
 * saem da mesma regra da cobrança. Aqui só se escolhe por onde imprimir, na
 * mesma ordem do recibo da comanda:
 *   1. Agente de Impressão online + impressora "caixa" → fila do caixa
 *   2. QZ Tray neste computador + impressora "caixa"   → ESC/POS direto
 *   3. Impressão do navegador                           → diálogo de impressão
 *
 * `body`: `{}` para a mesa inteira, ou `{ item_ids, pagador }` para a parte
 * de uma pessoa. Erros do backend (comanda fechada, item já pago) sobem.
 *
 * Nos caminhos 1 e 2 a impressão sai sozinha. No 3 nada é impresso aqui: volta
 * `imprimir()` para quem chamou disparar DEPOIS de fechar o próprio diálogo
 * (ver ConferenciaDialog), e `motivo` diz por que não saiu direto.
 */
export async function imprimirConferencia(tabId, body) {
  const printer = await getBranchPrinter("caixa");

  let agenteOnline = false;
  if (printer) {
    try {
      const { data } = await api.get("/print-agent/status");
      agenteOnline = !!data?.online;
    } catch { /* sem agente — tenta o QZ Tray */ }
    if (agenteOnline) {
      const { data } = await api.post(`/tabs/${tabId}/conferencia/imprimir`, body);
      return { via: "agente", total: data.total };
    }
  }

  const { data: conf } = await api.post(`/tabs/${tabId}/conferencia`, body);

  if (printer) {
    try {
      if (await isQZAvailable()) {
        await printLinhas(conf.linhas, printer);
        return { via: "qz", total: conf.total };
      }
    } catch (e) {
      console.warn("[conferencia] QZ Tray falhou, usando o navegador:", e?.message);
    }
  }

  const motivo = !printer
    ? "Nenhuma impressora com destino \"caixa\" cadastrada nesta filial (Configurações → Impressoras)."
    : "O Agente de Impressão está desligado no computador do caixa.";
  return {
    via: "navegador",
    total: conf.total,
    motivo,
    imprimir: () => imprimirNoNavegador(conf.linhas),
  };
}

function esc(s) {
  return String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

/** Desenha as linhas num iframe oculto e abre o diálogo de impressão.
 *  Iframe, e não janela nova: a janela seria bloqueada como pop-up, porque
 *  abre depois de uma chamada à API e não direto do clique. */
function imprimirNoNavegador(linhas) {
  const SIZE = { small: "1em", medium: "1.3em", big: "1.6em" };
  const html = (linhas || []).map(ln => {
    if (ln.t === "sep") return `<div class="sep"></div>`;
    if (ln.t === "cols") {
      return `<div class="cols${ln.bold ? " b" : ""}"><span>${esc(ln.left)}</span><span>${esc(ln.right)}</span></div>`;
    }
    return `<div style="text-align:${esc(ln.align || "left")};font-size:${SIZE[ln.size] || "1em"}"`
      + `${ln.bold ? ' class="b"' : ""}>${esc(ln.text)}</div>`;
  }).join("");

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>Conferência</title><style>
    /* Térmica pelo driver do Windows: traço fino vira cinza pontilhado e some.
       Fonte sem serifa, em negrito e preto puro é o que sai legível. */
    /* Sem largura fixa: 80 mm de rolo têm ~72 mm imprimíveis (58 mm, ~48 mm),
       e o que passa disso é cortado — foi o dígito dos centavos. O corpo
       ocupa a área que o driver informa, com folga à direita. */
    @page { margin: 0; }
    * { color: #000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; font-weight: 700;
           line-height: 1.3; margin: 0; padding: 1mm 4mm 1mm 1mm; }
    div { white-space: pre-wrap; }   /* recuo do adicional ("  Leite: Aveia") */
    .sep { border-top: 2px dashed #000; margin: 5px 0; }
    .cols { display: flex; justify-content: space-between; gap: 6px; }
    .cols span:first-child { overflow: hidden; }
    .cols span:last-child { white-space: nowrap; }
    .b { font-weight: 900; font-size: 1.15em; }
  </style></head><body>${html}</body></html>`);
  doc.close();
  const limpar = () => setTimeout(() => frame.remove(), 1000);
  frame.contentWindow.onafterprint = limpar;
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    // Safari não dispara afterprint de forma confiável.
    setTimeout(limpar, 60_000);
  }, 50);
}
